import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { http, formatError } from '@/lib/api';
import { toast } from 'sonner';
import { Phone, Sparkles, Clock, ShieldOff, Copy, Check, X } from 'lucide-react';

const DISPOSITIONS = [
  { id: 'no_answer', label: 'No answer' },
  { id: 'left_voicemail', label: 'Left voicemail' },
  { id: 'conversation', label: 'Conversation' },
  { id: 'wrong_number', label: 'Wrong number' },
  { id: 'appointment_booked', label: 'Appointment booked' },
  { id: 'follow_up_needed', label: 'Follow-up needed' },
  { id: 'not_interested', label: 'Not interested' },
  { id: 'do_not_call', label: 'Do not call' },
];

const OBJECTIONS = [
  'Not interested',
  'Already have an agent',
  'How did you get my number?',
  'We are waiting',
  'Commission is too high',
  'Just bring me a buyer',
  "We're selling it ourselves",
  'Call me later',
  'Take me off your list',
];

const TAGS = ['Motivated', 'Timing 30d', 'Timing 90d', 'Price concern', 'Referral asked', 'Needs valuation', 'Curious'];

function tryParseJSON(text) {
  if (!text) return null;
  const match = text.match(/\{[\s\S]*\}/);
  if (!match) return null;
  try { return JSON.parse(match[0]); } catch { return null; }
}

export default function CallSession() {
  const { id } = useParams();
  const nav = useNavigate();
  const [session, setSession] = useState(null);
  const [lead, setLead] = useState(null);
  const [script, setScript] = useState('');
  const [scriptLoading, setScriptLoading] = useState(false);
  const [notes, setNotes] = useState('');
  const [tags, setTags] = useState([]);
  const [temperature, setTemperature] = useState(null);
  const [followUp, setFollowUp] = useState('');
  const [obj, setObj] = useState(null); // {objection, response, follow_up, respect_optout}
  const [objLoading, setObjLoading] = useState(false);
  const [ending, setEnding] = useState(false);
  const [copied, setCopied] = useState(false);
  const [duration, setDuration] = useState(0);
  const startedAt = useRef(Date.now());

  // Load session on mount
  useEffect(() => {
    (async () => {
      try {
        const list = await http.get('/call/sessions');
        const s = list.data.find(x => x.id === id);
        if (!s) return toast.error('Session not found');
        setSession(s);
        const leadsRes = await http.get('/call/leads');
        const l = leadsRes.data.find(x => x.id === s.lead_id);
        setLead(l);
        startedAt.current = new Date(s.started_at).getTime();
      } catch (e) { toast.error(formatError(e)); }
    })();
  }, [id]);

  // Live timer
  useEffect(() => {
    const t = setInterval(() => setDuration(Math.floor((Date.now() - startedAt.current) / 1000)), 1000);
    return () => clearInterval(t);
  }, []);

  const generateScript = async () => {
    if (!lead) return;
    setScriptLoading(true); setScript('');
    try {
      const { data } = await http.post('/call/script', {
        lead_id: lead.id,
        lead_type: lead.lead_type,
        lead_name: lead.name,
        property_area: lead.address || undefined,
        goal: 'Book a listing or buyer consultation',
        tone: 'warm',
      });
      setScript(data.script);
    } catch (e) { toast.error(formatError(e)); }
    setScriptLoading(false);
  };

  const askObjection = async (o) => {
    setObjLoading(true); setObj({ objection: o, response: '', follow_up: '', respect_optout: false });
    try {
      const { data } = await http.post('/call/objection', {
        objection: o,
        lead_context: lead ? `${lead.lead_type} lead at ${lead.address || 'unknown address'}` : '',
        tone: 'warm',
      });
      const parsed = tryParseJSON(data.raw) || { response: data.raw, follow_up: '', respect_optout: /take me off|remove me|do not call|stop call/i.test(o) };
      setObj({ objection: o, ...parsed });
    } catch (e) { toast.error(formatError(e)); }
    setObjLoading(false);
  };

  const toggleTag = (t) => {
    setTags(tags.includes(t) ? tags.filter(x => x !== t) : [...tags, t]);
  };

  const endWith = async (dispId) => {
    if (!session) return;
    setEnding(true);
    try {
      await http.post(`/call/sessions/${session.id}/end`, {
        disposition: dispId,
        notes,
        tags,
        temperature,
        follow_up_at: followUp || undefined,
        duration_sec: duration,
      });
      toast.success('Call logged.');
      nav('/call');
    } catch (e) { toast.error(formatError(e)); }
    setEnding(false);
  };

  const copyScript = () => {
    navigator.clipboard.writeText(script);
    setCopied(true); setTimeout(() => setCopied(false), 1200);
  };

  const durationLabel = useMemo(() => {
    const m = String(Math.floor(duration / 60)).padStart(2, '0');
    const s = String(duration % 60).padStart(2, '0');
    return `${m}:${s}`;
  }, [duration]);

  if (!lead || !session) {
    return <div className="hq-eyebrow" style={{ color: 'var(--sage)' }}>Loading call session…</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header bar */}
      <div className="hq-card p-6 hq-fade-in" style={{ borderColor: 'var(--plum-soft)' }}>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="hq-plum-eyebrow">Live Call</p>
            <h1 className="font-serif text-3xl mt-1" style={{ color: 'var(--sage)' }} data-testid="session-lead-name">{lead.name}</h1>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-sm" style={{ color: 'var(--ink-soft)' }}>
              <a href={`tel:${lead.phone}`} className="inline-flex items-center gap-2 hq-btn-plum" data-testid="click-to-call-btn">
                <Phone size={13} /> {lead.phone_display || lead.phone}
              </a>
              <span className="hq-badge hq-badge-plum uppercase">{lead.lead_type}</span>
              <span className={`hq-badge ${lead.compliance === 'green' ? 'hq-badge-green' : lead.compliance === 'yellow' ? 'hq-badge-yellow' : 'hq-badge-red'}`}>
                {lead.compliance}
              </span>
              {lead.address && <span className="text-xs" style={{ color: 'var(--muted)' }}>· {lead.address}</span>}
            </div>
          </div>
          <div className="text-right">
            <div className="hq-eyebrow">Duration</div>
            <div className="font-serif text-3xl inline-flex items-center gap-2" style={{ color: 'var(--plum)' }}>
              <Clock size={16} /> <span data-testid="call-duration">{durationLabel}</span>
            </div>
            {lead.last_disposition && (
              <div className="mt-2 text-xs" style={{ color: 'var(--muted)' }}>Last outcome: {lead.last_disposition}</div>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        {/* Left column — script + objections */}
        <div className="lg:col-span-3 space-y-5">
          {/* Script */}
          <div className="hq-card p-6">
            <div className="flex items-center justify-between mb-3">
              <div className="hq-plum-eyebrow">AI Call Script</div>
              <div className="flex gap-2">
                {script && (
                  <button onClick={copyScript} className="hq-btn-ghost">
                    {copied ? <><Check size={12} className="inline mr-1"/> Copied</> : <><Copy size={12} className="inline mr-1"/> Copy</>}
                  </button>
                )}
                <button
                  className="hq-btn-plum"
                  onClick={generateScript}
                  disabled={scriptLoading}
                  data-testid="generate-script-btn"
                >
                  {scriptLoading ? 'Generating…' : <><Sparkles size={12} className="inline mr-1" /> Generate</>}
                </button>
              </div>
            </div>
            {scriptLoading && <div className="hq-shimmer h-40 rounded-sm" />}
            {!scriptLoading && !script && (
              <div className="text-sm py-8 text-center" style={{ color: 'var(--muted)' }}>
                Tap Generate for a permission-based opener + discovery + soft close.
              </div>
            )}
            {script && (
              <pre className="whitespace-pre-wrap font-serif text-base" style={{ color: 'var(--ink)', lineHeight: 1.75 }} data-testid="script-text">
                {script}
              </pre>
            )}
          </div>

          {/* Objection helper */}
          <div className="hq-card p-6">
            <div className="hq-plum-eyebrow mb-3">Objection Helper</div>
            <div className="flex flex-wrap gap-2 mb-4">
              {OBJECTIONS.map(o => (
                <button
                  key={o}
                  onClick={() => askObjection(o)}
                  className="text-xs px-3 py-2 border rounded-sm"
                  data-testid={`obj-${o.slice(0, 12).replace(/\s/g, '-')}`}
                  style={{ borderColor: 'var(--line)', color: 'var(--ink)', background: '#fff' }}
                >
                  {o}
                </button>
              ))}
            </div>
            {objLoading && <div className="hq-shimmer h-24 rounded-sm" />}
            {obj && !objLoading && (
              <div className="p-4 hq-fade-in" style={{ background: 'var(--plum-tint)', border: '1px solid var(--plum-soft)', borderRadius: 2 }} data-testid="objection-response">
                <div className="hq-plum-eyebrow mb-2">"{obj.objection}"</div>
                {obj.respect_optout && (
                  <div className="mb-3 text-xs px-3 py-2" style={{ background: '#fbe9e4', border: '1px solid #e1b3a6', color: 'var(--danger)', borderRadius: 2 }}>
                    Opt-out detected. Confirm removal, thank them, end the call. Do not rebut.
                  </div>
                )}
                {obj.response && (
                  <div className="mb-2">
                    <div className="hq-eyebrow mb-1">Your response</div>
                    <div className="font-serif text-base" style={{ color: 'var(--ink)', lineHeight: 1.6 }}>{obj.response}</div>
                  </div>
                )}
                {obj.follow_up && (
                  <div>
                    <div className="hq-eyebrow mb-1">Follow-up question</div>
                    <div className="font-serif text-base italic" style={{ color: 'var(--ink)', lineHeight: 1.6 }}>{obj.follow_up}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right column — notes + dispositions */}
        <div className="lg:col-span-2 space-y-5">
          <div className="hq-card p-6">
            <div className="hq-plum-eyebrow mb-3">Live Notes</div>
            <textarea
              className="hq-input"
              rows={7}
              placeholder="What did they say? What matters?"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              data-testid="live-notes"
            />
            <div className="mt-4">
              <div className="hq-eyebrow mb-2">Quick Tags</div>
              <div className="flex flex-wrap gap-2">
                {TAGS.map(t => (
                  <button
                    key={t}
                    onClick={() => toggleTag(t)}
                    data-testid={`tag-${t.replace(/\s/g, '-')}`}
                    className="text-xs px-3 py-1.5 border rounded-sm"
                    style={{
                      borderColor: tags.includes(t) ? 'var(--plum)' : 'var(--line)',
                      background: tags.includes(t) ? 'var(--plum-tint)' : '#fff',
                      color: tags.includes(t) ? 'var(--plum)' : 'var(--ink-soft)',
                    }}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-5">
              <div className="hq-eyebrow mb-2">Temperature</div>
              <div className="flex gap-2">
                {['hot', 'warm', 'cold'].map(t => (
                  <button
                    key={t}
                    onClick={() => setTemperature(t)}
                    data-testid={`temp-${t}`}
                    className="flex-1 py-2 text-xs uppercase tracking-widest"
                    style={{
                      background: temperature === t ? (t === 'hot' ? 'var(--danger)' : t === 'warm' ? 'var(--gold)' : 'var(--sage-soft)') : '#fff',
                      color: temperature === t ? '#fff' : 'var(--ink)',
                      border: '1px solid var(--line)',
                      borderRadius: 2,
                    }}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-5">
              <div className="hq-eyebrow mb-2">Follow-up date (optional)</div>
              <input
                type="datetime-local"
                className="hq-input"
                value={followUp}
                onChange={e => setFollowUp(e.target.value)}
                data-testid="follow-up-date"
              />
            </div>
          </div>

          <div className="hq-card p-6">
            <div className="hq-plum-eyebrow mb-3">End Call — Disposition</div>
            <div className="grid grid-cols-2 gap-2">
              {DISPOSITIONS.map(d => (
                <button
                  key={d.id}
                  onClick={() => endWith(d.id)}
                  disabled={ending}
                  data-testid={`disp-${d.id}`}
                  className="text-xs px-3 py-3 text-left border rounded-sm transition"
                  style={{
                    borderColor: d.id === 'do_not_call' ? 'var(--danger)' : 'var(--line)',
                    color: d.id === 'do_not_call' ? 'var(--danger)' : 'var(--ink)',
                    background: '#fff',
                  }}
                >
                  {d.id === 'do_not_call' && <ShieldOff size={11} className="inline mr-1" />}
                  {d.label}
                </button>
              ))}
            </div>
            <div className="mt-4 text-xs text-center" style={{ color: 'var(--muted)' }}>
              "Do not call" auto-adds this number to your permanent DNC list.
            </div>
          </div>

          <Link to="/call" className="hq-btn-ghost inline-flex items-center gap-2 w-full justify-center" data-testid="cancel-session-btn">
            <X size={12} /> Cancel without logging
          </Link>
        </div>
      </div>
    </div>
  );
}
